import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ShoutOut } from '@app/core/models/ShoutOut';
import { Track } from '@app/core/models/Track';
import { RadioWatcherService } from '@app/core/ngxs/radio-watcher.service';
import { DataResolved } from '@app/core/resolvers/generic-resolver.resolver';
import { AudioState } from '@app/core/states/audio.state';
import { RadioState } from '@app/core/states/radio.state';
import { ShoutOutsState } from '@app/core/states/shout-outs.state';
import { getBacTypeAccordingToName } from '@app/library/components/library-utils';
import { BacPlaylistModalComponent } from '@app/library/components/modals/bac-playlist-modal/bac-playlist-modal.component';
import { Bac } from '@app/library/models/bac.model';
import { SearchService } from '@app/library/services/search.service';
import {
  ResetFilterForBacCurrentlyEditing,
  SetFilterForBacCurrentlyEditing,
} from '@app/library/states/filters.actions';
import {
  AddDefaultTracksFailure,
  AddDefaultTracksRequest,
  AddDefaultTracksSuccess,
  TracksFailure,
  TracksRequest,
  TracksSuccess,
} from '@app/library/states/tracks.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import {
  MobileSidenavService,
  UiStoreHelperService,
} from '@radioking/shared/common-services';
import { SearchBarComponent } from '@radioking/ui/input';
import { isArray } from 'lodash';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs/operators';

@Component({
  selector: 'rk-library-mobile',
  templateUrl: './library-mobile.component.html',
  styleUrls: ['./library-mobile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LibraryMobileComponent implements OnInit, OnDestroy {
  currentBac$: Observable<Bac>;

  @Select(RadioState.currentRadioId)
  myRadioId$: Observable<number>;

  @Select(ShoutOutsState.shoutOuts)
  shoutOuts$: Observable<ShoutOut>;

  @Select(AudioState.currentTrackPlaying)
  currentTrack$: Observable<Track>;

  @ViewChild('searchBar')
  searchBar: SearchBarComponent;

  tracks$: Observable<Track[]>;

  tracksUnfiltered$: Observable<Track[]>;

  isLoading$: Observable<boolean>;

  isImportingDefaultTracks$: Observable<boolean>;

  searchSubject = new BehaviorSubject<string>('');

  subscription = new Subscription();

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly watcher: RadioWatcherService,
    private readonly helper: UiStoreHelperService,
    private readonly actions$: Actions,
    private readonly searchService: SearchService,
    private readonly mobileSidenavService: MobileSidenavService,
  ) {}

  ngOnInit(): void {
    this.currentBac$ = this.route.data.pipe(
      map<any, DataResolved<Bac>>(val => val.bacs),
      tap(() => {
        this.mobileSidenavService.close();
        this.clearSearch();
      }),
      switchMap(val => val.data$),
      filter(val => typeof val !== 'undefined'),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.isLoading$ = this.helper.getLoadingState(
      TracksRequest,
      [TracksFailure, TracksSuccess],
      true,
    );

    this.isImportingDefaultTracks$ = this.helper.getLoadingState(
      AddDefaultTracksRequest,
      [AddDefaultTracksSuccess, AddDefaultTracksFailure],
    );

    const bacRequestSubscription = this.currentBac$
      .pipe(
        distinctUntilChanged(),
        switchMap(bac =>
          this.watcher.dispatchAndWatchAsObservable(
            new TracksRequest(bac),
            this.actions$.pipe(
              ofActionSuccessful(
                ResetFilterForBacCurrentlyEditing,
                SetFilterForBacCurrentlyEditing,
              ),
            ),
          ),
        ),
      )
      .subscribe();

    this.subscription.add(bacRequestSubscription);

    const currentBacName$ = this.currentBac$.pipe(
      map(bac => getBacTypeAccordingToName(bac.name)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.tracksUnfiltered$ = currentBacName$.pipe(
      switchMap(name =>
        this.store.select(TracksState.tracksBac).pipe(map(fn => fn(name))),
      ),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.tracks$ = this.searchService
      .createTracksSearch(this.tracksUnfiltered$, this.searchSubject.asObservable())
      .pipe(
        filter(val => isArray(val)),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onChangeSearch(str: string) {
    if (typeof str === 'string') {
      this.searchSubject.next(str);
    }
  }

  clearSearch() {
    if (this.searchBar) {
      this.searchBar.value = '';
    }
    this.searchSubject.next('');
  }

  openBacPlaylistModal() {
    this.mobileSidenavService.open(BacPlaylistModalComponent);
  }
}
